import React, { useState } from 'react';
import ModalMenu, { ModalMenuOption } from '/Foundation/Common/code/Views/Foundation/Common/ModalMenu';
import Notification from '/Foundation/Common/code/Views/Foundation/Common/Notifications/Notification.js';
import { Translations, OrderLine } from './types.ts';

interface OrderlineProps {
  ol: OrderLine;
  i: number;
  isWithdrawn: boolean;
  setOrderLines?: (lines: OrderLine[]) => void;
  orderLines?: OrderLine[];
  Translations: Translations;
}

const Orderline: React.FC<OrderlineProps> = (props) => {
  const { ol, i, isWithdrawn, setOrderLines, orderLines, Translations } = props;

  // Quantity comes from JSON and may be a string at runtime despite the type declaration.
  const quantity = Number(ol.Quantity);
  const quantityLeft = Number(ol.QuantityLeft);

  const [showModal, setShowModal] = useState(false);

  const confirmSelection = (qty: number) => {
    const lines = [...orderLines!];
    const line = { ...lines[i] };
    line.QuantityToReturn = qty;
    line.QuantityError = isNaN(qty);
    lines[i] = line;
    setOrderLines!(lines);
    setTimeout(() => setShowModal(false), 300);
  };

  const handleSelect = () => {
    if (quantityLeft === 1) {
      confirmSelection(1);
    } else {
      setShowModal(true);
    }
  };

  const getModalContent = () => {
    if (!showModal) return null;

    const qtyOptions: ModalMenuOption[] = Array.from({ length: quantityLeft }, (_, idx) => ({
      Name: idx + 1,
      Value: idx + 1,
    }));
    qtyOptions.unshift({ Name: Translations.All, Value: quantityLeft });

    return (
      <ModalMenu
        setSelectedItem={(item: ModalMenuOption) => confirmSelection(item.Value as number)}
        mainTitle={Translations.SelectQuantity}
        placeholder={Translations.SearchPlaceholder}
        close={() => setTimeout(() => setShowModal(false), 300)}
        steps={1}
        step={1}
        options={qtyOptions}
        hideSearch={qtyOptions.length <= 12}
        secondaryButtonText={Translations.Cancel}
        secondaryButtonAction={() => setShowModal(false)}
      />
    );
  };

  return (
    <article className={`orderline ${!ol.IsWithdrawable ? 'disabled' : ''} d-lg-flex p-3 p-lg-4`}>
      <div className="product-image me-lg-5 me-xl-6 mb-6 mb-lg-0">
        {ol.Image && <img src={ol.Image} alt={ol.ImageAlt || ol.Title} />}
      </div>

      <div className="orderline-body d-flex">
        <div className="product-details d-flex">
          <div>
            <h5 className="heading5 mb-3">{ol.Title}</h5>
            <div className="paragraph--s mb-2 text-color--gray70">
              {Translations.ModelNumber}: <span>{isWithdrawn ? ol.Sku : ol.SKUNumber}</span>
            </div>
            <div className="paragraph--s mb-2 text-color--gray70">
              {Translations.Color}: <span>{ol.Color}</span>
            </div>
            {isWithdrawn && (
              <div className="paragraph--s mb-2 text-color--gray70">
                {Translations.Quantity}: <span>{ol.Quantity}</span>
              </div>
            )}
            {!isWithdrawn && (
              <div className="paragraph--s mb-2 text-color--gray70">
                {Translations.Quantity}:{' '}
                <span>
                  {ol.Quantity} ({Translations.Withdrawn} {ol.QuantityWithdrawn} {Translations.Returned}{' '}
                  {ol.QuantityReturned})
                </span>
              </div>
            )}
          </div>
          <div>
            <span className="product-price-value heading6 ms-3">{isWithdrawn ? ol.Total : ol.LineTotal}</span>
          </div>
        </div>

        {!isWithdrawn && (
          <>
            {!ol.IsWithdrawable && (
              <div className="d-flex already-withdrawn">
                <i className="le-icon-info"></i>
                <span className="heading6">{ol.Reason}</span>
              </div>
            )}
            {ol.IsWithdrawable && (
              <div className="product-selection-button d-flex mt-6 mt-lg-0" onClick={handleSelect}>
                <i
                  className={`selection-indicator fit-indicator ${ol.QuantityToReturn > 0 ? 'le-icon-check-circle confirmed' : 'le-icon-plus-circle'}`}
                />
                <span className="heading6 text-color--black fit-guide-span ms-3">
                  {ol.QuantityToReturn > 0
                    ? `${ol.QuantityToReturn} ${Translations.SelectedProducts}`
                    : Translations.SelectProduct}
                </span>
              </div>
            )}
          </>
        )}
      </div>
      {getModalContent()}
    </article>
  );
};

export default Orderline;
